import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ExternalLink } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";
import GooglePlayBadge from "@/components/GooglePlayBadge";

export default function Footer() {
  return (
    <footer className="bg-black border-t border-white/10 mt-32" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="md:col-span-4 space-y-5">
          <div className="flex items-center gap-3">
            <img
              src={COMPANY.logo}
              alt="VPS"
              className="h-14 w-14 object-contain bg-white p-1"
            />
            <div>
              <div className="font-display text-lg uppercase tracking-tight">
                Voltech <span className="text-[#e63946]">Power</span> Solutions
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                Achieve your Goals
              </div>
            </div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-sm">
            Hyderabad-based manufacturer of lithium battery solutions, solar
            street lights, electric vehicles and turnkey CSR-led energy
            infrastructure.
          </p>
          <div className="flex flex-col gap-2 text-sm text-zinc-300">
            <a
              href={COMPANY.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 hover:text-[#e63946] transition-colors"
              data-testid="footer-address-link"
            >
              <MapPin size={14} className="mt-1 text-[#e63946]" />
              <span>{COMPANY.address}</span>
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="flex items-center gap-2 hover:text-[#e63946] transition-colors"
              data-testid="footer-email-link"
            >
              <Mail size={14} className="text-[#e63946]" /> {COMPANY.email}
            </a>
            <a
              href={`tel:${COMPANY.phoneSales}`}
              className="flex items-center gap-2 hover:text-[#e63946] transition-colors"
              data-testid="footer-phone-sales"
            >
              <Phone size={14} className="text-[#e63946]" /> Sales · {COMPANY.phoneSales}
            </a>
            <a
              href={`tel:${COMPANY.phoneService}`}
              className="flex items-center gap-2 hover:text-[#e63946] transition-colors"
              data-testid="footer-phone-service"
            >
              <Phone size={14} className="text-[#e63946]" /> Service · {COMPANY.phoneService}
            </a>
          </div>
        </div>

        {/* Sitemap */}
        <div className="md:col-span-2">
          <div className="eyebrow mb-4">Site</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/" className="hover:text-[#e63946]" data-testid="footer-link-home">Home</Link></li>
            <li><Link to="/about" className="hover:text-[#e63946]" data-testid="footer-link-about">About</Link></li>
            <li><Link to="/services" className="hover:text-[#e63946]" data-testid="footer-link-services">Services</Link></li>
            <li><Link to="/products" className="hover:text-[#e63946]" data-testid="footer-link-products">Products</Link></li>
            <li><Link to="/lfp-inverter-batteries" className="hover:text-[#e63946]" data-testid="footer-link-lfp">LFP Inverter Batteries</Link></li>
            <li><Link to="/solar-street-lights" className="hover:text-[#e63946]" data-testid="footer-link-solar">Solar Street Lights</Link></li>
            <li><Link to="/custom-battery-packs" className="hover:text-[#e63946]" data-testid="footer-link-custom">Custom Battery Packs</Link></li>
            <li><Link to="/projects" className="hover:text-[#e63946]" data-testid="footer-link-projects">Projects & Updates</Link></li>
            <li><Link to="/csr" className="hover:text-[#e63946]" data-testid="footer-link-csr">CSR</Link></li>
            <li><Link to="/contact" className="hover:text-[#e63946]" data-testid="footer-link-contact">Contact</Link></li>
            <li><Link to="/privacy" className="hover:text-[#e63946]" data-testid="footer-link-privacy">Privacy Policy</Link></li>
          </ul>
        </div>

        {/* Services */}
        <div className="md:col-span-3">
          <div className="eyebrow mb-4">Capabilities</div>
          <ul className="space-y-2 text-sm text-zinc-300">
            {SERVICES.slice(0, 8).map((s) => (
              <li key={s.name}>{s.name}</li>
            ))}
          </ul>
        </div>

        {/* External */}
        <div className="md:col-span-3">
          <div className="eyebrow mb-4">Connect</div>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#25d366]"
                data-testid="footer-whatsapp-direct"
              >
                <ExternalLink size={14} /> WhatsApp Chat
              </a>
            </li>
            <li>
              <a
                href={COMPANY.whatsappChannel}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#25d366]"
                data-testid="footer-whatsapp-channel"
              >
                <ExternalLink size={14} /> WhatsApp Channel
              </a>
            </li>
            <li>
              <a
                href={COMPANY.indiamart}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#e63946]"
                data-testid="footer-indiamart-link"
              >
                <ExternalLink size={14} /> IndiaMART Storefront
              </a>
            </li>
          </ul>
          <div className="mt-6 p-3 border border-white/10 bg-[#121215]">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
              GSTIN
            </div>
            <div className="font-mono text-xs text-white mt-1" data-testid="footer-gstin">
              {COMPANY.gstin}
            </div>
          </div>
          <div className="mt-4">
            <GooglePlayBadge testId="footer-google-play" />
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} VOLTECH POWER SOLUTIONS · All rights reserved.
          </div>
          <div className="font-mono uppercase tracking-[0.2em]">
            Made in Hyderabad · India
          </div>
        </div>
      </div>
    </footer>
  );
}
